(function () {
  var BASE = "/apps/messages";
  var TOKEN_KEY = "messaging_portal_token";

  // Liquid's `customer`, rendered server-side into `window.rbxCustomer` on every request
  // (see layout/theme.liquid), decides whether the UI shows a signed-in shopper. The app
  // proxy may only ADD to that — an email, a legacy session — never take it away.
  //
  // It used to be the other way round, and that failed closed twice over: Shopify delivers
  // an empty `logged_in_customer_id` on proxy requests for stores on the current customer
  // accounts, and the app itself can be down. Either one left someone who was signed in at
  // the cart and checkout reading as signed out in the header, on the profile page and in
  // the chat, which is exactly what it looked like from the outside.
  var liquidCustomer = window.rbxCustomer || null;

  var state = {
    open: false, // dropdown open/closed; the page view is always visible
    // Whether anyone is signed in. Must NOT be inferred from `email`: a shopper identified
    // by their Shopify customer account has no email on our side (reading one would need
    // the read_customers scope), so an email-based test reports them as logged out.
    loggedIn: !!liquidCustomer,
    email: liquidCustomer ? liquidCustomer.email : null,
    authSource: liquidCustomer ? "shopify" : null, // "shopify" | "legacy"
    error: null,
  };

  var dropdown = null;
  var activeTriggerBtn = null;
  var pageRoot = document.getElementById("rbx-account-page-root");

  function getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  // Sign-in is Shopify's customer account. Credentials are only ever entered on
  // Shopify's hosted login, which is what makes this dropdown, the chat widget and
  // checkout one account rather than three.
  function accountLoginUrl() {
    return (
      "/account/login?return_url=" +
      encodeURIComponent(window.location.pathname + window.location.search)
    );
  }

  // Only a pre-switch custom-auth session still has a token; a Shopify-backed one is
  // identified by the signed `logged_in_customer_id` on the app-proxy request itself.
  function withToken(path) {
    var token = getToken();
    if (!token) return path;
    return path + (path.indexOf("?") === -1 ? "?" : "&") + "token=" + encodeURIComponent(token);
  }

  function setToken(token) {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  }

  function api(path, options) {
    options = options || {};
    return fetch(BASE + path, {
      method: options.method || "GET",
      headers: { "Content-Type": "application/json" },
      body: options.body ? JSON.stringify(options.body) : undefined,
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw data;
        return data;
      });
    });
  }

  function getTriggerButtons() {
    return Array.prototype.slice.call(document.querySelectorAll(".rbx-account-button"));
  }

  function updateTriggerButtons() {
    getTriggerButtons().forEach(function (btn) {
      if (state.loggedIn) {
        var who = state.email ? "signed in as " + state.email : "signed in";
        btn.classList.add("is-logged-in");
        btn.setAttribute("aria-label", "Account — " + who);
        btn.title = who.charAt(0).toUpperCase() + who.slice(1);
      } else {
        btn.classList.remove("is-logged-in");
        btn.setAttribute("aria-label", "Log in");
        btn.title = "Log in";
      }
    });
  }

  // --- Sync with the separate chat widget's session (shared localStorage
  // token, but the account UI here and the chat bubble are otherwise
  // independent — logging into one just keeps the other's session in step). ---
  function notifyChatWidget() {
    if (window.MessagingWidget && typeof window.MessagingWidget.syncSession === "function") {
      window.MessagingWidget.syncSession();
    }
  }

  function subscribeToChatWidget() {
    if (window.MessagingWidget && typeof window.MessagingWidget.onAuthChange === "function") {
      window.MessagingWidget.onAuthChange(function (data) {
        state.loggedIn = !!data.loggedIn || !!liquidCustomer;
        state.email =
          (data.loggedIn ? data.email : null) ||
          (liquidCustomer && liquidCustomer.email) ||
          null;
        state.error = null;
        updateTriggerButtons();
        renderAll();
      });
      return;
    }
    // messaging.js may not have loaded yet — retry briefly.
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (window.MessagingWidget && typeof window.MessagingWidget.onAuthChange === "function") {
        clearInterval(timer);
        subscribeToChatWidget();
      } else if (attempts > 20) {
        clearInterval(timer);
      }
    }, 250);
  }

  function checkSession() {
    // No early return on a missing token: Shopify signs the logged-in customer onto the
    // proxy request, so the shopper can be signed in with nothing stored locally.
    return api(withToken("/session"))
      .then(function (data) {
        state.loggedIn = true;
        // Display only. The server holds no email for Shopify-identified shoppers, so fall
        // back to what Liquid rendered for this request.
        state.email =
          data.email || (window.rbxCustomer && window.rbxCustomer.email) || null;
        state.authSource = data.source || "shopify";
        updateTriggerButtons();
        return true;
      })
      .catch(function (err) {
        // Only an actual "no session" answer retires a legacy token. A network failure
        // means the app is unreachable, not that the shopper is signed out; clearing the
        // token there would turn every outage into a permanent logout.
        var refused = err && typeof err === "object" && err.loggedIn === false;
        if (refused && getToken()) setToken(null);
        if (liquidCustomer) {
          // Shopify says they are signed in. The proxy not agreeing is the proxy's problem.
          state.loggedIn = true;
          state.email = liquidCustomer.email;
          state.authSource = "shopify";
        } else {
          state.loggedIn = false;
          state.email = null;
          state.authSource = null;
        }
        updateTriggerButtons();
        return state.loggedIn;
      });
  }

  // --- Dropdown (header icon) ---

  function ensureDropdown() {
    if (dropdown) return dropdown;
    dropdown = document.createElement("div");
    dropdown.className = "rbx-account-dropdown";
    dropdown.setAttribute("role", "dialog");
    document.body.appendChild(dropdown);
    document.addEventListener("click", function (e) {
      if (!state.open) return;
      if (
        dropdown.contains(e.target) ||
        activeTriggerBtn === e.target ||
        (activeTriggerBtn && activeTriggerBtn.contains(e.target))
      ) {
        return;
      }
      closeDropdown();
    });
    window.addEventListener("resize", function () {
      if (state.open) closeDropdown();
    });
    return dropdown;
  }

  function positionDropdown(triggerBtn) {
    var rect = triggerBtn.getBoundingClientRect();
    dropdown.style.top = rect.bottom + 8 + "px";
    dropdown.style.right = Math.max(8, window.innerWidth - rect.right) + "px";
  }

  function openDropdown(triggerBtn) {
    ensureDropdown();
    activeTriggerBtn = triggerBtn;
    state.open = true;
    state.error = null;
    positionDropdown(triggerBtn);
    dropdown.classList.add("rbx-account-dropdown--open");
    renderAll();
    if (!state.loggedIn) checkSession().then(renderAll);
  }

  function closeDropdown() {
    state.open = false;
    if (dropdown) dropdown.classList.remove("rbx-account-dropdown--open");
    activeTriggerBtn = null;
  }

  function toggleDropdown(triggerBtn) {
    if (state.open && activeTriggerBtn === triggerBtn) {
      closeDropdown();
    } else {
      openDropdown(triggerBtn);
    }
  }

  // --- Shared render logic: both the dropdown and the full profile page
  // (#rbx-account-page-root, see templates/page.profile.json) render the
  // exact same login/signup/account views into whichever container is
  // currently mounted, so there is only one implementation of the auth UI. ---

  function renderAll() {
    if (dropdown) renderInto(dropdown, "dropdown");
    if (pageRoot) renderInto(pageRoot, "page");
  }

  function renderInto(container, variant) {
    container.innerHTML = "";
    if (state.loggedIn) {
      renderAccountView(container, variant);
    } else {
      renderAuthForm(container, variant);
    }
  }

  function prefix(variant) {
    return variant === "page" ? "rbx-account-page" : "rbx-account-dropdown";
  }

  var PAGE_TABS = [
    { id: "account", label: "Account" },
    { id: "orders", label: "Orders" },
    { id: "discounts", label: "Discounts" },
    { id: "chats", label: "Chats" }
  ];

  function currentTab() {
    var hash = (window.location.hash || "").replace("#", "");
    for (var i = 0; i < PAGE_TABS.length; i++) {
      if (PAGE_TABS[i].id === hash) return hash;
    }
    return "account";
  }

  // The profile page is a set of places you go, not one long scroll. Driven by the hash so
  // the back button works and a tab can be linked to directly.
  function renderAccountPageTabs(container) {
    var nav = element("nav", "rbx-account-tabs");
    nav.setAttribute("aria-label", "Account sections");
    var panel = element("div", "rbx-account-panel");
    var active = currentTab();

    PAGE_TABS.forEach(function (tab) {
      var link = element("a", "rbx-account-tab" + (tab.id === active ? " rbx-account-tab--active" : ""), tab.label);
      link.href = "#" + tab.id;
      if (tab.id === active) link.setAttribute("aria-current", "page");
      nav.appendChild(link);
    });

    container.appendChild(nav);
    container.appendChild(panel);
    renderPanel(panel, active);
  }

  function renderPanel(panel, tab) {
    panel.innerHTML = "";
    if (tab === "orders") return renderOrders(panel);
    if (tab === "discounts") return renderDiscounts(panel);
    if (tab === "chats") return renderChats(panel);
    return renderAccountDetails(panel);
  }

  function renderAccountDetails(panel) {
    var section = element("section", "rbx-account-details");
    section.appendChild(element("h2", "rbx-account-page__header", "Account information"));

    var row = element("div", "rbx-account-details__row");
    row.appendChild(element("span", "rbx-account-details__label", "Signed in as"));
    row.appendChild(element("span", "rbx-account-details__value", state.email || "Your account"));
    section.appendChild(row);

    var message = element("button", "rbx-account-details__action", "Questions? Message us");
    message.type = "button";
    message.onclick = openWidget;
    section.appendChild(message);

    var logout = element("button", "rbx-account-details__logout", "Log out");
    logout.type = "button";
    logout.onclick = doLogout;
    section.appendChild(logout);

    panel.appendChild(section);
  }

  function openWidget(conversationId) {
    closeDropdown();
    var widget = window.MessagingWidget;
    if (widget && typeof widget.open === "function") {
      widget.open(typeof conversationId === "string" ? conversationId : undefined);
      return;
    }
    var bubble = document.querySelector(".mw-bubble");
    if (bubble) bubble.click();
  }

  function renderChats(panel) {
    var section = element("section", "rbx-account-chats");
    section.appendChild(element("h2", "rbx-account-page__header", "Chats"));
    var list = element("div", "rbx-account-chats__list");
    list.appendChild(element("p", "rbx-account-chats__empty", "Loading\u2026"));
    section.appendChild(list);
    panel.appendChild(section);

    fetch("/apps/messages/conversation", { headers: { Accept: "application/json" } })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        list.innerHTML = "";
        var conversations = (data && data.conversations) || [];
        if (!conversations.length) {
          list.appendChild(element("p", "rbx-account-chats__empty", "No chats yet."));
          var start = element("button", "rbx-order-action", "Questions? Message us");
          start.type = "button";
          start.onclick = function () { openWidget(); };
          list.appendChild(start);
          return;
        }
        conversations.forEach(function (c) {
          var row = element("button", "rbx-account-chat");
          row.type = "button";
          row.onclick = function () { openWidget(c.id); };

          var main = element("div", "rbx-account-chat__main");
          main.appendChild(element("span", "rbx-account-chat__title", c.title || "Chat"));
          main.appendChild(element("span", "rbx-account-chat__snippet",
            c.lastMessage || "No messages yet"));
          row.appendChild(main);

          if (c.unread) {
            row.appendChild(element("span", "rbx-account-chat__unread", String(c.unread)));
          }
          list.appendChild(row);
        });
      })
      .catch(function () {
        list.innerHTML = "";
        list.appendChild(element("p", "rbx-account-chats__empty", "Couldn\u2019t load your chats just now."));
      });
  }

  function doLogout() {
    var token = getToken();
    (token
      ? api("/auth/logout", { method: "POST", body: { token: token } }).catch(function () {})
      : Promise.resolve()
    ).then(function () {
      setToken(null);
      state.loggedIn = false;
      state.email = null;
      // A Shopify-backed session is a Shopify cookie — clearing local state alone would
      // leave them signed in at checkout, so let Shopify end it.
      if (state.authSource === "shopify") {
        window.location.href = "/account/logout";
        return;
      }
      state.authSource = null;
      updateTriggerButtons();
      notifyChatWidget();
      renderAll();
    });
  }

  function renderAccountView(container, variant) {
    var p = prefix(variant);

    if (variant === "page") {
      renderAccountPageTabs(container);
      return;
    }

    var header = document.createElement("div");
    header.className = p + "__header";
    header.textContent = "Signed in as";
    container.appendChild(header);

    var emailEl = document.createElement("div");
    emailEl.className = p + "__email";
    emailEl.textContent = state.email || "Your account";
    container.appendChild(emailEl);

    var messageBtn = document.createElement("button");
    messageBtn.type = "button";
    messageBtn.className = p + "__link";
    messageBtn.textContent = "Questions? Message us";
    messageBtn.onclick = function () {
      closeDropdown();
      if (window.MessagingWidget && typeof window.MessagingWidget.open === "function") {
        window.MessagingWidget.open();
      } else {
        var bubble = document.querySelector(".mw-bubble");
        if (bubble) bubble.click();
      }
    };
    container.appendChild(messageBtn);

    if (variant === "dropdown" && window.location.pathname.indexOf("/pages/my-account") === -1) {
      var profileLink = document.createElement("a");
      profileLink.href = "/pages/my-account";
      profileLink.className = p + "__link";
      profileLink.textContent = "My orders";
      container.appendChild(profileLink);
    }


    var logoutBtn = document.createElement("button");
    logoutBtn.type = "button";
    logoutBtn.className = p + "__logout";
    logoutBtn.textContent = "Log out";
    logoutBtn.onclick = doLogout;
    container.appendChild(logoutBtn);
  }

  // Orders are matched to a portal account by email — see the messaging-app's
  // orders/create webhook, which links a Shopify order to a PortalUser as
  // soon as it's placed under the same email. This just displays what's
  // already linked; it doesn't create the link itself.
  // --- Discounts: every reward this shopper holds, in one place ---
  //
  // The code is also shown on the order it came from, but an order card is where you go to
  // ask "what did I buy", not "what can I spend". Anything claimable or spendable lives
  // here so it is findable months later without scrolling order history.

  function discountCard(reward) {
    var card = element("article", "rbx-discount");

    var head = element("div", "rbx-discount__head");
    head.appendChild(element("span", "rbx-discount__value", "$5 off"));
    head.appendChild(element("span", "rbx-discount__for", "Order " + reward.orderNumber));
    card.appendChild(head);

    if (reward.claimed && reward.code) {
      var reveal = element("button", "rbx-discount__reveal", "View code");
      reveal.type = "button";

      var code = element("button", "rbx-discount__code", reward.code);
      code.type = "button";
      code.title = "Copy code";
      code.hidden = true;
      code.onclick = function() {
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(reward.code).then(function() {
          var original = code.textContent;
          code.textContent = "Copied";
          setTimeout(function() { code.textContent = original; }, 1200);
        });
      };

      reveal.onclick = function() {
        reveal.hidden = true;
        code.hidden = false;
        code.focus();
      };

      card.appendChild(reveal);
      card.appendChild(code);
      card.appendChild(element("p", "rbx-discount__terms",
        "Use on any order of $50 or more. One use. Never expires."));
      return card;
    }

    card.appendChild(element("p", "rbx-discount__terms",
      "Leave a review on what you bought, then claim your code. $50 minimum, never expires."));

    var claim = element("button", "rbx-discount__claim", "Claim my $5");
    claim.type = "button";
    claim.onclick = function() {
      claim.disabled = true;
      claim.textContent = "Checking\u2026";
      fetch("/apps/messages/reward", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId: String(reward.orderId) })
      }).then(function(response) {
        return response.json().then(function(data) { return { ok: response.ok, data: data }; });
      }).then(function(result) {
        if (result.ok && result.data && result.data.ok) {
          var fresh = discountCard({
            orderId: reward.orderId,
            orderNumber: reward.orderNumber,
            claimed: true,
            code: result.data.code
          });
          if (card.parentNode) card.parentNode.replaceChild(fresh, card);
          return;
        }
        claim.disabled = false;
        claim.textContent = "Claim my $5";
        var existing = card.querySelector(".rbx-discount__error");
        if (existing) existing.remove();
        card.appendChild(element("p", "rbx-discount__error",
          (result.data && result.data.error) || "Couldn\u2019t do that right now \u2014 try again."));
      }).catch(function() {
        claim.disabled = false;
        claim.textContent = "Claim my $5";
        card.appendChild(element("p", "rbx-discount__error", "Check your connection and try again."));
      });
    };
    card.appendChild(claim);
    return card;
  }

  // The welcome promo is its own card: it isn't tied to an order, it expires, and it is
  // the one a brand-new shopper is most likely to be looking for.
  function welcomeCard(welcome) {
    var card = element("article", "rbx-discount rbx-discount--welcome");

    var head = element("div", "rbx-discount__head");
    head.appendChild(element("span", "rbx-discount__value", welcome.percentOff + "% off"));
    head.appendChild(element("span", "rbx-discount__for", "Your first order"));
    card.appendChild(head);

    if (welcome.expired) {
      card.appendChild(element("p", "rbx-discount__terms", "This welcome code has expired."));
      return card;
    }

    var reveal = element("button", "rbx-discount__reveal", "View code");
    reveal.type = "button";

    var code = element("button", "rbx-discount__code", welcome.code);
    code.type = "button";
    code.title = "Copy code";
    code.hidden = true;
    code.onclick = function() {
      if (!navigator.clipboard) return;
      navigator.clipboard.writeText(welcome.code).then(function() {
        var original = code.textContent;
        code.textContent = "Copied";
        setTimeout(function() { code.textContent = original; }, 1200);
      });
    };
    reveal.onclick = function() { reveal.hidden = true; code.hidden = false; code.focus(); };

    card.appendChild(reveal);
    card.appendChild(code);

    var days = Math.max(0, Math.ceil((new Date(welcome.expiresAt) - Date.now()) / 86400000));
    var when = new Date(welcome.expiresAt).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric"
    });
    card.appendChild(element("p", "rbx-discount__terms",
      "Use on your first order. Expires in " + days + " day" + (days === 1 ? "" : "s") +
      " \u2014 " + when + "."));

    return card;
  }

  function renderDiscounts(container) {
    var section = element("section", "rbx-account-discounts");
    var heading = element("h2", "rbx-account-page__header", "Discounts");
    section.appendChild(heading);

    var list = element("div", "rbx-account-discounts__list");
    list.appendChild(element("p", "rbx-account-discounts__empty", "Loading\u2026"));
    section.appendChild(list);
    container.appendChild(section);

    Promise.all([
      fetch("/apps/messages/reward", { headers: { Accept: "application/json" } })
        .then(function(r) { return r.json(); }),
      fetch("/apps/messages/welcome", { headers: { Accept: "application/json" } })
        .then(function(r) { return r.json(); })
        .catch(function() { return null; })
    ])
      .then(function(both) {
        var data = both[0];
        var welcome = both[1] && both[1].welcome;
        list.innerHTML = "";
        if (welcome) list.appendChild(welcomeCard(welcome));
        var rewards = (data && data.rewards) || [];

        // Only delivered orders can earn one, and a claimed code stays listed forever so
        // it can be found again later.
        var relevant = rewards.filter(function(r) { return r.claimed || r.delivered; });
        relevant.sort(function(a, b) {
          if (a.claimed !== b.claimed) return a.claimed ? -1 : 1;
          return 0;
        });

        if (!relevant.length) {
          if (!welcome) {
            list.appendChild(element("p", "rbx-account-discounts__empty",
              "No discounts yet. Once an order is delivered, leave a review and $5 off your next order shows up here."));
          }
          return;
        }
        relevant.forEach(function(reward) { list.appendChild(discountCard(reward)); });
      })
      .catch(function() {
        list.innerHTML = "";
        list.appendChild(element("p", "rbx-account-discounts__empty",
          "Couldn\u2019t load your discounts just now."));
      });
  }

  function renderOrders(container) {
    var section = document.createElement("section");
    section.className = "rbx-account-orders";
    var heading = document.createElement("h2");
    heading.className = "rbx-account-page__header";
    heading.textContent = "Order history";
    section.appendChild(heading);
    var list = document.createElement("div");
    list.className = "rbx-account-orders__list";
    list.textContent = "Loading orders…";
    section.appendChild(list);
    container.appendChild(section);
    var nativeOrders = document.getElementById("rbx-native-orders");
    if (nativeOrders) {
      try {
        var parsed = JSON.parse(nativeOrders.textContent);
        renderOrderList(list, parsed);
        // Fetched after the list is already on screen rather than before it. The orders
        // render from Liquid with no round trip; blocking that on a second request would
        // trade a visible delay for a detail most people never look at.
        loadCancelRequests(function() { renderOrderList(list, parsed); });
        return;
      } catch (e) {}
    }
    api(withToken("/orders")).then(function(data) {
      renderOrderList(list, data.orders || []);
      loadCancelRequests(function() { renderOrderList(list, data.orders || []); });
    })
      .catch(function() {
        list.textContent = "We couldn't load your orders. ";
        var retry = document.createElement("button");
        retry.type = "button"; retry.className = "rbx-order-action"; retry.textContent = "Try again";
        retry.onclick = function() { section.remove(); renderOrders(container); };
        list.appendChild(retry);
      });
  }

  function element(tag, cls, text) {
    var el = document.createElement(tag); el.className = cls;
    if (text !== undefined) el.textContent = text;
    return el;
  }

  function renderOrderList(list, orders) {
    list.innerHTML = "";
    if (!orders.length) {
      list.appendChild(element("p", "rbx-account-orders__empty", "No orders yet. Your purchased items and totals will appear here."));
      var browse = element("a", "rbx-order-action", "Browse items"); browse.href = "/collections/all"; list.appendChild(browse); return;
    }
    orders.forEach(function(order) {
      var card = element("article", "rbx-account-order");
      var top = element("div", "rbx-account-order__top");
      top.appendChild(element("h3", "rbx-account-order__name", order.name || "Order"));
      top.appendChild(element("span", "rbx-account-order__date", order.createdAt ? new Date(order.createdAt).toLocaleDateString(undefined, {year:"numeric",month:"short",day:"numeric"}) : ""));
      card.appendChild(top);
      var fulfilled = String(order.fulfillmentStatus || "").toLowerCase() === "fulfilled";
      var badges = element("div", "rbx-account-order__badges");
      if (order.financialStatus) badges.appendChild(element("span", "rbx-account-order__badge", formatStatus(order.financialStatus)));
      if (order.fulfillmentStatus) {
        badges.appendChild(element("span", "rbx-account-order__badge" + (fulfilled ? " rbx-account-order__badge--sent" : ""), fulfillmentLabel(order.fulfillmentStatus)));
      }
      card.appendChild(badges);
      var items = element("div", "rbx-order-items");
      (order.lineItems || []).forEach(function(item) {
        var row = element("div", "rbx-order-item");
        if (item.image) { var img = element("img", "rbx-order-item__image"); img.src = item.image; img.alt = ""; img.loading = "lazy"; row.appendChild(img); }
        var details = element("div", "rbx-order-item__details");
        details.appendChild(element("strong", "", item.title));
        details.appendChild(element("span", "rbx-order-item__meta", "Quantity: " + (item.quantity || 1) + " · Digital item"));
        row.appendChild(details); items.appendChild(row);
      });
      card.appendChild(items);
      var username = order.username || (order.lineItems || []).map(function(item) { return item.username; }).filter(Boolean)[0];
      if (username) card.appendChild(element("p", "rbx-order-username", "Roblox username: " + username));
      else if (order.note) card.appendChild(element("p", "rbx-order-username", "Order note: " + order.note));
      // The "any problems" prompt is deliberately gated on the order actually being sent.
      // Asking someone about problems with an item we haven't delivered yet just invites
      // confusion, so before fulfilment we state the delivery window instead.
      var note = element("div", "rbx-order-status" + (fulfilled ? " rbx-order-status--sent" : ""));
      note.appendChild(element("p", "rbx-order-status__title", fulfilled ? "Your order has been delivered" : "Your order is on the way"));
      note.appendChild(element("p", "rbx-order-status__text", fulfilled
        ? "For Roblox limiteds, check your in-game trades to accept the item. Everything else is already done on our side \u2014 nothing more is needed from you."
        : "Limiteds are delivered in-game within 24 hours of your order, and within 48 hours at the very latest. This page updates as soon as it\u2019s sent."));
      card.appendChild(note);

      var footer = element("div", "rbx-order-footer");
      if (order.total !== null && order.total !== undefined) footer.appendChild(element("div", "rbx-account-order__total", "Total " + formatMoney(order.total, order.currency)));
      var actions = element("div", "rbx-order-actions");
      if (order.url) { var detailLink = element("a", "rbx-order-detail", "View order"); detailLink.href = order.url; actions.appendChild(detailLink); }
      var message = element("button", "rbx-order-action" + (fulfilled ? " rbx-order-action--primary" : ""), fulfilled ? "Any problems? Message us" : "Message about " + (order.name || "this order")); message.type = "button";
      message.onclick = function() {
        if (window.MessagingWidget && typeof window.MessagingWidget.open === "function") window.MessagingWidget.open();
        else { var bubble = document.querySelector(".mw-bubble"); if (bubble) bubble.click(); }
      };
      actions.appendChild(message);

      // Review reward. Only on delivered orders — there is nothing to review before the
      // item has actually arrived, and the server enforces the same rule.
      if (fulfilled && order.id) actions.appendChild(rewardButton(order));

      footer.appendChild(actions); card.appendChild(footer);

      // Cancellation. The mirror image of the reward: only before it's been sent.
      var cancelledAlready = String(order.financialStatus || "").toLowerCase() === "voided" ||
        String(order.fulfillmentStatus || "").toLowerCase() === "restocked";
      if (order.id && !fulfilled && !cancelledAlready) card.appendChild(cancelControl(order));

      list.appendChild(card);
    });
  }

  // --- Cancellation requests ---
  //
  // Asking to cancel is not cancelling. Nothing here voids an order or moves money; it
  // records the ask and puts it on the order's chat thread for a human to action. The
  // button disappears once an order is fulfilled, because by then the item has been
  // traded in-game and there is nothing left to call back — the server enforces that
  // independently, so hiding the button is a courtesy, not the control.

  var cancelRequests = null; // orderId -> status, once loaded

  function loadCancelRequests(done) {
    api(withToken("/cancel-request"))
      .then(function (data) {
        var map = {};
        (data.requests || []).forEach(function (r) { map[String(r.orderId)] = r.status; });
        cancelRequests = map;
        if (done) done();
      })
      .catch(function () { cancelRequests = {}; });
  }

  function cancelStatusLabel(status) {
    if (status === "CANCELLED") return "Order cancelled";
    if (status === "DECLINED") return "Cancellation declined \u2014 message us";
    return "Cancellation requested \u2014 we\u2019ll be in touch";
  }

  function cancelControl(order) {
    var wrap = element("div", "rbx-order-cancel");
    var existing = cancelRequests && cancelRequests[String(order.id)];

    if (existing) {
      wrap.appendChild(element("p", "rbx-order-cancel__status", cancelStatusLabel(existing)));
      return wrap;
    }

    var button = element("button", "rbx-order-action rbx-order-action--quiet", "Request cancellation");
    button.type = "button";
    button.onclick = function () { openCancelForm(wrap, button, order); };
    wrap.appendChild(button);
    return wrap;
  }

  function openCancelForm(wrap, button, order) {
    button.hidden = true;
    var form = element("div", "rbx-cancel-form");
    form.appendChild(element("p", "rbx-cancel-form__title", "Cancel " + (order.name || "this order") + "?"));
    form.appendChild(element("p", "rbx-cancel-form__note",
      "We\u2019ll review it and refund you if the item hasn\u2019t been sent yet. Tell us why if it helps."));

    var reason = document.createElement("textarea");
    reason.className = "rbx-cancel-form__reason";
    reason.rows = 3;
    reason.maxLength = 500;
    reason.placeholder = "Reason (optional)";
    form.appendChild(reason);

    var row = element("div", "rbx-cancel-form__actions");
    var send = element("button", "rbx-order-action rbx-order-action--primary", "Send request");
    send.type = "button";
    var back = element("button", "rbx-order-action rbx-order-action--quiet", "Never mind");
    back.type = "button";
    back.onclick = function () { form.remove(); button.hidden = false; };

    send.onclick = function () {
      send.disabled = true;
      back.disabled = true;
      send.textContent = "Sending\u2026";
      api(withToken("/cancel-request"), {
        method: "POST",
        body: { orderId: String(order.id), reason: reason.value },
      })
        .then(function (data) {
          if (!cancelRequests) cancelRequests = {};
          cancelRequests[String(order.id)] = data.status || "OPEN";
          wrap.innerHTML = "";
          wrap.appendChild(element("p", "rbx-order-cancel__status", cancelStatusLabel(data.status || "OPEN")));
        })
        .catch(function (err) {
          send.disabled = false;
          back.disabled = false;
          send.textContent = "Send request";
          var existingError = form.querySelector(".rbx-cancel-form__error");
          if (existingError) existingError.remove();
          form.appendChild(element("p", "rbx-cancel-form__error",
            (err && err.error) || "Couldn\u2019t send that \u2014 try again."));
        });
    };

    row.appendChild(send);
    row.appendChild(back);
    form.appendChild(row);
    wrap.appendChild(form);
    reason.focus();
  }

  // --- Review reward: $5 off a $50+ order, one per delivered order ---

  function showCode(host, code, revealed) {
    var box = element("div", "rbx-order-code");
    box.appendChild(element("span", "rbx-order-code__label", "Your $5 code"));

    var value = element("button", "rbx-order-code__value", code);
    value.type = "button";
    value.title = "Copy";

    // Straight after claiming it is shown, since they just asked for it. Revisiting the
    // page later requires the reveal.
    if (!revealed) {
      var reveal = element("button", "rbx-discount__reveal", "View code");
      reveal.type = "button";
      value.hidden = true;
      reveal.onclick = function() { reveal.hidden = true; value.hidden = false; };
      box.appendChild(reveal);
    }
    value.onclick = function() {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(code).then(function() {
          value.textContent = "Copied";
          setTimeout(function() { value.textContent = code; }, 1200);
        });
      }
    };
    box.appendChild(value);
    box.appendChild(element("span", "rbx-order-code__note", "Valid on any order of $50 or more. Never expires."));
    host.appendChild(box);
  }

  function rewardButton(order) {
    var wrap = element("div", "rbx-order-reward");
    var button = element("button", "rbx-order-action", "Left a review? Get $5 off");
    button.type = "button";
    button.onclick = function() {
      button.disabled = true;
      button.textContent = "Checking\u2026";
      fetch("/apps/messages/reward", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId: String(order.id) })
      }).then(function(response) {
        return response.json().then(function(data) { return { ok: response.ok, data: data }; });
      }).then(function(result) {
        if (result.ok && result.data && result.data.ok) {
          wrap.innerHTML = "";
          showCode(wrap, result.data.code, true);
          return;
        }
        button.disabled = false;
        button.textContent = "Left a review? Get $5 off";
        wrap.appendChild(element("p", "rbx-order-reward__error",
          (result.data && result.data.error) || "Couldn\u2019t do that right now \u2014 try again."));
      }).catch(function() {
        button.disabled = false;
        button.textContent = "Left a review? Get $5 off";
        wrap.appendChild(element("p", "rbx-order-reward__error", "Check your connection and try again."));
      });
    };
    wrap.appendChild(button);
    return wrap;
  }

  // Shopify's raw fulfilment states are warehouse language ("unfulfilled"). Shoppers here
  // only care whether the item has actually been traded to them yet.
  function fulfillmentLabel(raw) {
    var s = String(raw || "").toLowerCase();
    if (s === "fulfilled") return "Order sent";
    if (s === "partial" || s === "partially_fulfilled") return "Partly sent";
    return "Preparing";
  }

  function formatStatus(raw) {
    return String(raw)
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, function (c) {
        return c.toUpperCase();
      });
  }

  function formatMoney(amount, currency) {
    var n = parseFloat(amount);
    if (isNaN(n)) return amount;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency || "USD",
      }).format(n);
    } catch (e) {
      return "$" + n.toFixed(2);
    }
  }

  // The email/password + Google form that used to live here is gone. Shopify's own
  // customer accounts are the site's only login now, so this just hands off to Shopify's
  // hosted sign-in and comes back. Social sign-in (Google) is configured on Shopify's
  // side under Settings -> Customer accounts -> sign-in options.
  function renderAuthForm(container, variant) {
    var p = prefix(variant);

    var header = document.createElement("div");
    header.className = p + "__header";
    header.textContent = "Log in";
    container.appendChild(header);

    var intro = document.createElement("p");
    intro.className = p + "__intro";
    intro.textContent =
      "One account for your orders, the chat and checkout.";
    container.appendChild(intro);

    var loginLink = document.createElement("a");
    loginLink.href = accountLoginUrl();
    loginLink.className = p + "__submit";
    loginLink.textContent = "Log in or sign up";
    container.appendChild(loginLink);
  }

  function attachTriggerButtons() {
    getTriggerButtons().forEach(function (btn) {
      if (btn.dataset.rbxBound) return;
      btn.dataset.rbxBound = "true";
      btn.addEventListener("click", function (e) {
        // Straight to the profile rather than a dropdown. The account page renders the
        // login form itself when signed out, so this is the right destination either way
        // and doesn't have to wait on checkSession() to know which state we're in.
        e.preventDefault();
        if (window.location.pathname.indexOf("/pages/my-account") === -1) {
          window.location.href = "/pages/my-account";
        }
      });
    });
  }

  function init() {
    attachTriggerButtons();
    // Paint the signed-in state from Liquid before any network call, so the header icon
    // and the profile page are never briefly wrong (or permanently wrong, if the app
    // is unreachable).
    updateTriggerButtons();
    window.addEventListener("hashchange", function () {
      if (pageRoot && state.loggedIn) renderAll();
    });
    checkSession().then(renderAll);
    subscribeToChatWidget();
    if (pageRoot) renderAll();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
